
import { Coordinates, WikiSpot } from "../types";
import { getDistance } from "./geometryUtils";
import i18n from "../i18n";

const getWikiApiUrl = (): string => {
  const lang = (i18n.resolvedLanguage || i18n.language || "no").toLowerCase();
  const wikiLang = lang.startsWith("en") ? "en" : "no";
  return `https://${wikiLang}.wikipedia.org/w/api.php`;
};

// Keywords to exclude from results (lowercase)
// NOTE: Avoid filtering infrastructure that can be tourist attractions (bridges, tunnels, roads, stations)
const EXCLUDED_KEYWORDS = [
  "skole", "videregående", "gymnas", "universitet", "høgskole", "campus", "school", "college", "university", // Education
  "fotball", "stadion", "idrett", "sport", "klubb", "forening", "football", "stadium", "sports", "club", // Sports
  "barnehage", "sykehjem", "omsorg", "aldershjem", "helsehus", "kindergarten", "nursing home", "care", // Care
  "borettslag", "sameie", "bolig", "felt", "gate", "residential", "housing", "street", // Generic residential
  "kryss", "viadukt", "junction", "viaduct", // Infrastructure (only truly boring ones)
  "kraftverk", "transformator", "power plant", "transformer" // Utility
];

export interface WikiResult {
  pageid: number;
  title: string;
  extract: string;
  thumbnail?: { source: string; width: number; height: number };
  coordinates?: { lat: number; lon: number }[];
  distance?: number;
  fullUrl: string;
  length?: number;
}

export let lastWikiDebug = {
  lastUrl: "",
  lastResponse: null as any,
  lastError: null as any,
  timestamp: 0,
  results: [] as WikiResult[]
};

/**
 * Checks if a spot contains banned keywords in title or extract.
 */
const isBoringSpot = (title: string, extract: string): boolean => {
  const text = (title + " " + extract).toLowerCase();
  return EXCLUDED_KEYWORDS.some(keyword => text.includes(keyword));
};

const queryWiki = async (lat: number, lon: number, radius: number = 2000, requireThumbnail: boolean = true): Promise<WikiSpot[]> => {
  const params = new URLSearchParams({
    action: "query",
    generator: "geosearch",
    ggscoord: `${lat}|${lon}`,
    ggsradius: radius.toString(),
    ggslimit: "20",
    prop: "pageimages|extracts|coordinates|info",
    pithumbsize: "400",
    exintro: "1",
    explaintext: "1",
    inprop: "url",
    format: "json",
    origin: "*"
  });

  const url = `${getWikiApiUrl()}?${params.toString()}`;
  lastWikiDebug.lastUrl = url;

  try {
    const response = await fetch(url);
    if (!response.ok) return [];

    const data = await response.json();
    if (!data.query || !data.query.pages) return [];

    const pages = Object.values(data.query.pages) as any[];
    return pages.map(page => {
      let lat = 0, lon = 0;
      if (page.coordinates && page.coordinates[0]) {
        lat = page.coordinates[0].lat;
        lon = page.coordinates[0].lon;
      }
      return {
        pageid: page.pageid,
        title: page.title,
        extract: page.extract || "",
        thumbnail: page.thumbnail,
        location: { latitude: lat, longitude: lon },
        url: page.fullurl
      };
    })
      .filter(s => s.location.latitude !== 0)
      .filter(s => !requireThumbnail || !!s.thumbnail)
      .filter(s => !isBoringSpot(s.title, s.extract));

  } catch (e) {
    return [];
  }
};

export const fetchAttractions = async (coords: Coordinates): Promise<WikiSpot[]> => {
  // 6 km radius as requested
  return await queryWiki(coords.latitude, coords.longitude, 6000);
};

export const fetchWikiContext = async (coords: Coordinates): Promise<void> => {
  if (!coords) return;
  const lat = coords.latitude;
  const lon = coords.longitude;

  // Specific params for Context Debugging: include length, strict radius
  const params = new URLSearchParams({
    action: "query",
    generator: "geosearch",
    ggscoord: `${lat}|${lon}`,
    ggsradius: "500", // Strict 500m radius
    ggslimit: "20", // Get more candidates to filter
    prop: "pageimages|extracts|coordinates|info",
    pithumbsize: "400",
    exintro: "1",
    explaintext: "1",
    inprop: "url|length", // Request length
    format: "json",
    origin: "*"
  });

  const url = `${getWikiApiUrl()}?${params.toString()}`;

  lastWikiDebug.lastUrl = url;
  lastWikiDebug.timestamp = Date.now();
  lastWikiDebug.lastError = null;
  lastWikiDebug.results = [];

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Wiki API Error: ${response.status}`);
    }

    const data = await response.json();
    lastWikiDebug.lastResponse = data;

    if (!data.query || !data.query.pages) {
      console.debug("Wiki Scan: Found 0 raw items.");
      return;
    }

    const pages = Object.values(data.query.pages) as any[];
    console.debug(`Wiki Scan: Found ${pages.length} raw items.`);

    // Quality Gate & Mapping
    const candidates: WikiResult[] = pages.map(page => {
      let dist = 0;
      if (page.coordinates && page.coordinates[0]) {
        dist = getDistance(lat, lon, page.coordinates[0].lat, page.coordinates[0].lon);
      }
      return {
        pageid: page.pageid,
        title: page.title,
        extract: page.extract,
        thumbnail: page.thumbnail,
        coordinates: page.coordinates,
        distance: Math.round(dist),
        fullUrl: page.fullurl,
        length: page.length
      };
    }).filter(res => {
      // Filter Logic
      if (!res.thumbnail) return false; // Must have image
      if ((res.length || 0) < 1500) return false; // Must be substantive (>1500 bytes)
      if ((res.distance || 0) > 500) return false; // Strict radius
      if (isBoringSpot(res.title, res.extract)) return false; // Content filter
      return true;
    });

    console.debug(`Wiki Scan: ${candidates.length} passed quality filter.`);

    // Ranking: Sort by length descending (Longest article = most significant)
    candidates.sort((a, b) => (b.length || 0) - (a.length || 0));

    // Limit: Top 1
    const bestMatch = candidates.slice(0, 1);
    lastWikiDebug.results = bestMatch;

    // bestMatch lagret i lastWikiDebug for feilsøking via devtools

  } catch (error: any) {
    lastWikiDebug.lastError = error.message;
    console.warn("Wiki Context Fetch Failed:", error);
  }
};

export const fetchWikiForPath = async (path: Coordinates[]): Promise<WikiSpot[]> => {
  if (!path || path.length < 2) return [];

  // Calculate total route distance for adaptive sampling
  let totalRouteDistance = 0;
  for (let i = 0; i < path.length - 1; i++) {
    totalRouteDistance += getDistance(path[i].latitude, path[i].longitude, path[i + 1].latitude, path[i + 1].longitude);
  }

  // Adaptive sampling: every ~20km, max 30 samples (was: every 5km, max 10)
  const maxSamples = Math.min(Math.ceil(totalRouteDistance / 20000), 30);
  const sampleInterval = Math.max(5000, totalRouteDistance / Math.max(maxSamples, 1));

  const samplePoints: Coordinates[] = [];
  let distAcc = 0;

  samplePoints.push(path[0]); // Always include start

  for (let i = 0; i < path.length - 1; i++) {
    const dist = getDistance(path[i].latitude, path[i].longitude, path[i + 1].latitude, path[i + 1].longitude);
    distAcc += dist;
    if (distAcc > sampleInterval) {
      samplePoints.push(path[i + 1]);
      distAcc = 0;
    }
  }
  samplePoints.push(path[path.length - 1]); // Always include end

  const finalSamples = samplePoints.length > maxSamples
    ? samplePoints.filter((_, i) => i % Math.ceil(samplePoints.length / maxSamples) === 0)
    : samplePoints;

  // Use 5km radius and don't require thumbnail for path queries
  const allResults = await Promise.all(finalSamples.map(p => queryWiki(p.latitude, p.longitude, 5000, false)));

  // Deduplicate by pageid
  const seen = new Set<number>();
  const uniqueSpots: WikiSpot[] = [];

  allResults.flat().forEach(spot => {
    if (!seen.has(spot.pageid)) {
      seen.add(spot.pageid);
      uniqueSpots.push(spot);
    }
  });

  return uniqueSpots;
};

/**
 * Translates WikiSpots to another language using Wikipedia's langlinks API (free, no AI).
 * For each spot, looks up the equivalent article in the target language Wikipedia,
 * then fetches title, extract, thumbnail and URL from that language's Wikipedia.
 * Returns a Map from original pageid → translated WikiSpot. Spots without a translation
 * are simply omitted from the Map (caller should fall back to original).
 */
export const fetchWikiTranslations = async (
  spots: WikiSpot[],
  targetLang: 'en' | 'de'
): Promise<Map<number, WikiSpot>> => {
  const result = new Map<number, WikiSpot>();
  if (!spots.length) return result;

  // Step 1: Get langlinks for all spots (batch up to 50 pageids per request)
  const pageids = spots.map(s => s.pageid);
  const langlinksMap = new Map<number, string>(); // original pageid → target-lang title

  for (let i = 0; i < pageids.length; i += 50) {
    const batch = pageids.slice(i, i + 50);
    const params = new URLSearchParams({
      action: 'query',
      pageids: batch.join('|'),
      prop: 'langlinks',
      lllang: targetLang,
      lllimit: '500',
      format: 'json',
      origin: '*'
    });

    try {
      const resp = await fetch(`https://no.wikipedia.org/w/api.php?${params}`);
      if (!resp.ok) continue;
      const data = await resp.json();
      if (!data.query?.pages) continue;

      for (const [pid, page] of Object.entries(data.query.pages) as [string, any][]) {
        const links = page.langlinks;
        if (links && links.length > 0) {
          langlinksMap.set(Number(pid), links[0]['*']);
        }
      }
    } catch { /* skip batch on error */ }
  }

  if (langlinksMap.size === 0) return result;

  // Step 2: Fetch full articles from target-language Wikipedia
  const titlesToFetch = Array.from(langlinksMap.entries()); // [originalPid, targetTitle][]
  const titleToOriginalPid = new Map<string, number>();
  titlesToFetch.forEach(([pid, title]) => titleToOriginalPid.set(title, pid));

  const targetBaseUrl = `https://${targetLang}.wikipedia.org/w/api.php`;

  for (let i = 0; i < titlesToFetch.length; i += 50) {
    const batch = titlesToFetch.slice(i, i + 50);
    const titles = batch.map(([, t]) => t).join('|');

    const params = new URLSearchParams({
      action: 'query',
      titles,
      prop: 'pageimages|extracts|info',
      pithumbsize: '400',
      exintro: '1',
      explaintext: '1',
      inprop: 'url',
      format: 'json',
      origin: '*'
    });

    try {
      const resp = await fetch(`${targetBaseUrl}?${params}`);
      if (!resp.ok) continue;
      const data = await resp.json();
      if (!data.query?.pages) continue;

      for (const page of Object.values(data.query.pages) as any[]) {
        if (!page.title) continue;
        const originalPid = titleToOriginalPid.get(page.title);
        if (originalPid === undefined) continue;

        // Find the original spot to preserve location
        const originalSpot = spots.find(s => s.pageid === originalPid);
        if (!originalSpot) continue;

        result.set(originalPid, {
          pageid: originalPid,
          title: page.title,
          extract: page.extract || '',
          thumbnail: page.thumbnail || originalSpot.thumbnail,
          location: originalSpot.location,
          url: page.fullurl || `https://${targetLang}.wikipedia.org/wiki/${encodeURIComponent(page.title)}`
        });
      }
    } catch { /* skip batch on error */ }
  }

  return result;
};
